import { forwardRef } from 'react';
import { Info, CircleCheck, TriangleAlert, CircleAlert, X, type LucideIcon } from 'lucide-react';
import { cn } from './cn';

export type AlertSeverity = 'info' | 'success' | 'warning' | 'error';

export interface AlertProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  /** Visual tone. Note: 'warning' uses the orange theme; 'error' is the only red one. */
  severity?: AlertSeverity;
  /** Bold heading rendered above the body. */
  title?: React.ReactNode;
  /** Dismiss handler — renders a top-right close button when provided. */
  onClose?: () => void;
  /** Trailing action (e.g. a button), rendered at the end of the row. */
  action?: React.ReactNode;
  /** Custom leading icon, or `false` to hide the default severity icon. */
  icon?: React.ReactNode | false;
  children?: React.ReactNode;
}

interface SeverityConfig {
  Icon: LucideIcon;
  container: string;
  text: string;
}

const severities: Record<AlertSeverity, SeverityConfig> = {
  info: {
    Icon: Info,
    container: 'bg-info/10 border-info/30',
    text: 'text-info',
  },
  success: {
    Icon: CircleCheck,
    container: 'bg-success/10 border-success/30',
    text: 'text-success',
  },
  // Project rule: warnings use the primary orange, never amber/red.
  warning: {
    Icon: TriangleAlert,
    container: 'bg-primary/10 border-primary/30',
    text: 'text-primary',
  },
  // The only severity that uses red.
  error: {
    Icon: CircleAlert,
    container: 'bg-danger/10 border-danger/30',
    text: 'text-danger',
  },
};

/**
 * Inline alert / banner — replaces MUI <Alert />.
 * Soft tonal style per severity. Warnings render orange (project rule); only
 * `error` is red. Pass `title` for a heading, `action` for a trailing control,
 * `onClose` for a dismiss button, and `icon={false}` to drop the leading icon.
 */
export const Alert = forwardRef<HTMLDivElement, AlertProps>(
  ({ severity = 'info', title, onClose, action, icon, className, children, ...props }, ref) => {
    const { Icon, container, text } = severities[severity];
    const showIcon = icon !== false;

    return (
      <div
        ref={ref}
        role="alert"
        className={cn(
          'flex gap-3 rounded-xl border p-3.5 text-sm',
          container,
          text,
          className,
        )}
        {...props}
      >
        {showIcon && (
          <span className="mt-px shrink-0">
            {icon ?? <Icon size={20} strokeWidth={2} aria-hidden="true" />}
          </span>
        )}

        <div className="min-w-0 flex-1 text-content">
          {title && <div className={cn('font-semibold text-sm', text)}>{title}</div>}
          {children && (
            <div className={cn('text-sm', title && 'mt-0.5')}>{children}</div>
          )}
        </div>

        {action && <div className="shrink-0 self-center">{action}</div>}

        {onClose && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className={cn(
              '-mr-1 -mt-0.5 shrink-0 self-start rounded-lg p-1 transition-colors',
              'hover:bg-primary/10 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
              text,
            )}
          >
            <X size={18} strokeWidth={2} />
          </button>
        )}
      </div>
    );
  },
);

Alert.displayName = 'Alert';
